/**
 * Admin Layout
 *
 * Dark navy sidebar with admin-specific navigation.
 * Branding: "LexiReview Admin" with Shield icon.
 *
 * Sections:
 *   OVERVIEW    — Admin Dashboard
 *   PLATFORM    — Users, Platform Health, Financial Ops, Organizations
 *   LEGAL INTELLIGENCE — Legal Brain, Learning Queue, Rule Database
 */

import { ReactNode, useEffect, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  Shield,
  Users,
  HeartPulse,
  DollarSign,
  Brain,
  Lightbulb,
  Database,
  Building2,
  Bell,
  LifeBuoy,
  Rocket,
  ExternalLink,
  LogOut,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
} from "lucide-react"

type NavItem = { path: string; label: string; icon: typeof Shield; external?: boolean }
import { authService, User } from "../../services/auth.service"

interface AdminLayoutProps {
  children: ReactNode
}

const overviewNav = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
]

const platformNav = [
  { path: "/users", label: "User Management", icon: Users },
  { path: "/platform", label: "Platform Health", icon: HeartPulse },
  { path: "/finance", label: "Financial Ops", icon: DollarSign },
  { path: "/organizations", label: "Organizations", icon: Building2 },
]

const intelligenceNav = [
  { path: "/legal-brain", label: "Legal Brain", icon: Brain },
  { path: "/learning", label: "Learning Queue", icon: Lightbulb },
  { path: "/rule-db", label: "Rule Database", icon: Database },
]

const engagementNav = [
  { path: "/notifications", label: "Notifications", icon: Bell },
  { path: "/support", label: "Support Tickets", icon: LifeBuoy },
]

// Growth engine (Lexi Forge) — a separate app, reached via the SSO bridge so the
// super-admin session carries over (no second login).
const growthNav: NavItem[] = [
  { path: "https://lexireview.in/forge/sso", label: "Lexi Forge", icon: Rocket, external: true },
]

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [currentUser, setCurrentUser] = useState<User | null>(null)

  useEffect(() => {
    authService.getCurrentUser().then(setCurrentUser).catch(() => {})
  }, [])

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/"
    return location.pathname.startsWith(path)
  }

  const handleLogout = () => {
    authService.logout().then(() => {
      window.location.href = "https://lexireview.in/login"
    })
  }

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon

    if (item.external) {
      return (
        <a
          key={item.path}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center rounded-lg px-3 py-2.5 text-sm font-medium font-body transition-all duration-200 text-slate-300 hover:bg-white/5 hover:text-white"
          title={isCollapsed ? item.label : undefined}
        >
          <Icon className="h-5 w-5 shrink-0 text-slate-400" />
          {!isCollapsed && <span className="ml-3">{item.label}</span>}
          {!isCollapsed && <ExternalLink className="ml-auto h-3.5 w-3.5 text-slate-500" />}
        </a>
      )
    }

    const active = isActive(item.path)

    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center rounded-lg px-3 py-2.5 text-sm font-medium font-body transition-all duration-200 ${
          active
            ? "bg-white/10 text-white border-l-2 border-blue-400"
            : "text-slate-300 hover:bg-white/5 hover:text-white"
        }`}
        title={isCollapsed ? item.label : undefined}
      >
        <Icon
          className={`h-5 w-5 shrink-0 ${
            active ? "text-blue-400" : "text-slate-400"
          }`}
        />
        {!isCollapsed && <span className="ml-3">{item.label}</span>}
        {active && !isCollapsed && (
          <div className="ml-auto h-1.5 w-1.5 rounded-full bg-blue-400" />
        )}
      </Link>
    )
  }

  const renderSection = (title: string, items: NavItem[]) => (
    <div>
      {!isCollapsed && (
        <p className="px-3 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider font-heading">
          {title}
        </p>
      )}
      <div className="space-y-1">
        {items.map(renderNavItem)}
      </div>
    </div>
  )

  const getDisplayName = (user: User) => {
    if (user.firstName && user.lastName)
      return `${user.firstName} ${user.lastName}`.trim()
    if (user.firstName) return user.firstName
    return user.email?.split("@")[0] || "Admin"
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={`flex flex-col h-screen border-r border-navy-800 bg-navy-950 transition-all duration-300 ${
          isCollapsed ? "w-16" : "w-64"
        }`}
      >
        {/* Logo Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-navy-800">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700">
              <Shield className="w-4.5 h-4.5 text-white" />
            </div>
            {!isCollapsed && (
              <div className="flex flex-col">
                <span className="text-sm font-bold text-white font-heading leading-tight">
                  LexiReview
                </span>
                <span className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest font-heading">
                  Admin
                </span>
              </div>
            )}
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 rounded-md hover:bg-white/5 text-slate-400"
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-6">
          {renderSection("Overview", overviewNav)}
          {renderSection("Platform", platformNav)}
          {renderSection("Legal Intelligence", intelligenceNav)}
          {renderSection("Engagement", engagementNav)}
          {renderSection("Growth", growthNav)}
        </nav>

        {/* User + Logout */}
        <div className="p-3 border-t border-navy-800">
          {currentUser && !isCollapsed && (
            <div className="px-3 py-2 mb-2">
              <p className="text-sm font-medium text-white font-body truncate">
                {getDisplayName(currentUser)}
              </p>
              <p className="text-xs text-slate-400 font-body truncate">
                {currentUser.email}
              </p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center w-full rounded-lg px-3 py-2.5 text-sm font-medium font-body text-slate-300 hover:bg-white/5 hover:text-white transition-all duration-200"
            title={isCollapsed ? "Sign Out" : undefined}
          >
            <LogOut className="h-5 w-5 shrink-0 text-slate-400" />
            {!isCollapsed && <span className="ml-3">Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  )
}
